/**
 * manage/strings — the manage page's own chrome, in the page's language.
 *
 * SAME RULES AS `blocks/one-stop/strings.ts`, and the same reasons:
 * - one keyed catalog resolved against the document locale, so the page speaks
 *   ONE language rather than two;
 * - `view.texts` (partner-editable, locale-resolved server-side) ALWAYS wins
 *   where it carries a key — this catalog is the renderer's chrome, never a
 *   substitute for authored content;
 * - plural categories come from `Intl.PluralRules`, numbers from
 *   `Intl.NumberFormat` (never `n === 1`, never a number pasted into English
 *   word order);
 * - every locale entry carries EVERY key, so a locale degrades to ONE visible
 *   fallback rather than a hole per literal.
 *
 * ui-config: a closed UI vocabulary. No seed, mock or fictional content — no
 * name, no time, no reference, no booking. Every value is a label this page
 * would show over ANY tenant's booking.
 *
 * `de-CH` is LAYERED over `de` (Swiss deltas only: «…» quotes, no ß), matching
 * how the server's own table is layered.
 */

/** ui-config */
const EN = {
  "state.loading": "Opening your booking…",

  "dead.invalid.title": "This link is no longer valid",
  "dead.invalid.body":
    "The link may have been mistyped, or it may belong to a booking that no longer exists.",
  "dead.expired.title": "This link has expired",
  "dead.expired.body": "Links from older confirmation emails stop working after a while.",
  "dead.revoked.title": "This link was withdrawn",
  "dead.revoked.body": "It was replaced or switched off, so it no longer opens your booking.",
  "dead.unreachable.title": "We can’t load your booking right now",
  "dead.unreachable.body": "This is on our side. Please try again in a moment.",
  "dead.noLink.title": "Open your booking from your confirmation",
  "dead.noLink.body": "This page needs the link in your confirmation email or text message.",
  "dead.help": "The newest confirmation message for this booking has a working link.",
  "dead.retry": "Try again",

  "stale.title": "Your booking moved on",
  "stale.body": "Load it again to carry on.",
  "stale.action": "Load my booking again",

  "tombstone.title": "A newer version of this booking exists",
  "tombstone.body":
    "This is how the booking looked when this link was sent. Open the newest confirmation message to make changes.",

  "status.pending": "Pending",
  "status.unconfirmed": "Not yet confirmed",
  "status.confirmed": "Confirmed",
  "status.rescheduled": "Moved",
  "status.cancelled": "Cancelled",
  "status.completed": "Completed",
  "status.no_show": "Missed",
  "status.checked_in": "Checked in",
  "status.waitlisted": "In the line",
  "status.payment_pending": "Payment pending",

  "section.when": "When",
  "section.where": "Where",
  "section.who": "Who you’ll see",
  "section.what": "What you told us",
  "section.you": "Your details",
  "section.payment": "Payment",
  "section.event": "Your tickets",
  "section.queue": "Your place in the line",
  "section.key": "Door code",
  "section.history": "Changes to this booking",
  "section.actions": "Manage this booking",

  "when.moved": "Moved from {t}",
  "when.local": "Your local time: {t}",
  "when.duration": "{n} min",

  "where.join": "Join the video call",
  "where.joinLater": "The joining link appears here at {t}.",
  "where.passcode": "Passcode {code}",
  "where.callYou": "We’ll call you on {number}",
  "where.callYouNoNumber": "We’ll call you",
  "where.callUs": "Please call {number}",
  "where.callUsNoNumber": "Please call us",
  "where.map": "Open in maps",

  "who.pending": "Not assigned yet",

  "you.masked": "Partly hidden until you confirm it’s you.",
  "you.reveal": "Show my details",
  "answers.masked": "Hidden until you confirm it’s you",
  "answers.file.scanning": "Being checked",
  "answers.file.blocked": "This file couldn’t be checked",
  "answers.download": "Download",

  "verify.title": "Confirm it’s you",
  "verify.intro": "We’ll send a one-time code to the contact details on this booking.",
  "verify.introRegistration": "We’ll send a one-time code to the contact details on this registration.",
  "verify.send": "Send my code",
  "verify.sent": "We sent a code to {target}.",
  "verify.code": "Enter the code",
  "verify.submit": "Confirm",
  "verify.resend": "Send another code",
  "verify.wait": "You can ask for another code in {n} s.",
  "verify.email": "Send it by email",
  "verify.sms": "Send it by text message",
  "verify.error": "That code isn’t right, or it has expired. Ask for a new one.",
  "verify.exhausted": "Too many tries. Please contact us to carry on.",
  "verify.unavailable": "We can’t send a code for this booking. Please contact us.",
  "verify.unavailableRegistration": "We can’t send a code for this registration. Please contact us.",

  "action.reschedule": "Move this booking",
  "action.cancel": "Cancel this booking",
  /* Event registrations (CC 0355); English only, the other catalogs fall back here. */
  "action.cancelRegistration": "Cancel this registration",
  "action.ics": "Add to my calendar",
  "action.pdf": "Download the summary",
  "action.export": "Download my booking data",
  "action.bookAnother": "Book something else",
  "action.history": "See what changed",
  "action.optOut": "Stop reminders for this booking",
  "action.optIn": "Send me reminders again",
  "action.optedOut": "Reminders are off for this booking.",

  "resch.title": "Pick a new time",
  "resch.staff": "Who you’ll see",
  "resch.anyStaff": "Anyone available",
  "resch.none": "No times are free in this window.",
  "resch.from": "Show times from",
  "resch.loading": "Loading times…",
  "resch.loadingFrom": "Loading times from {day}…",
  "resch.refreshing": "Updating these times…",
  "resch.stale": "These times are from {day} onwards and can’t be picked right now.",
  "resch.noneFrom": "No free times were found starting {day}. Try a later date.",
  "resch.laterHint": "Need a later day? Change the date above.",
  "resch.staffNote": "Each time shows who you’d see.",
  "resch.zoneNote": "Dates and times are local to {zone}, where the appointment takes place.",
  "resch.holding": "Holding this time…",
  "resch.releasing": "Releasing the time you were holding…",
  "resch.releaseFailed": "We couldn’t release the time you’re holding, so nothing has changed yet.",
  "resch.releasePending": "Your new choice will apply once that time is released.",
  "resch.releaseRetry": "Try releasing it again",
  "resch.closeAnyway": "Close anyway",
  "resch.closeNote": "If you close now, that time stays held for you until it runs out.",
  "resch.staffMismatch": "We can’t show times with only the colleague you chose right now, so none can be picked. Try again, or choose Anyone available.",
  "resch.confirming": "Moving your booking… Please keep this open until it finishes.",
  "resch.refreshingBooking": "Loading the current version of your booking…",
  "resch.refreshFailed": "We couldn’t load the current version of your booking, so this time can’t be confirmed yet.",
  "resch.refreshRetry": "Load it again",
  "resch.holdExpired": "The time we were holding for you has run out, so it can’t be confirmed. Pick it again to hold it again, or choose another time.",
  "resch.moveUnknown": "We didn’t hear back about moving your booking, so we can’t tell whether it moved. Nothing will be sent again from here. Close this and reload the page to see your booking’s current time.",
  "resch.more": "Show more times",
  "resch.held": "Held for you until {t}",
  "resch.confirm": "Confirm this time",
  "resch.moveFromTo": "Move from {from} to {to}?",
  "resch.moveFromToStaff": "Move from {from} to {to}, with {staff}?",
  "resch.reason": "Why are you moving it?",
  "resch.remaining.one": "You can move this booking {n} more time.",
  "resch.remaining.other": "You can move this booking {n} more times.",
  "resch.done": "Your booking has moved",
  "resch.newLink.email": "We’ve emailed you the link to your updated booking.",
  "resch.newLink.sms": "We’ve texted you the link to your updated booking.",

  "cancel.title": "Cancel this booking?",
  "cancel.body": "This can’t be undone. You’d need to book again.",
  "cancel.reason": "Why are you cancelling?",
  "cancel.reasonRequired": "Please tell us why you’re cancelling.",
  "cancel.deleteData": "Also ask us to delete the details you gave for this booking",
  "cancel.confirm": "Yes, cancel it",
  "cancel.keep": "Keep my booking",
  "cancel.titleRegistration": "Cancel this registration?",
  "cancel.bodyRegistration": "This can’t be undone. You’d need to register again.",
  "cancel.keepRegistration": "Keep my registration",
  "cancel.deleteDataRegistration": "Also ask us to delete the details you gave for this registration",

  "conflict.title": "This booking changed while you were here",
  "conflict.body": "We’ve loaded the current version. Please check it and confirm again.",

  "event.seats.one": "{n} seat",
  "event.seats.other": "{n} seats",
  "event.holders": "Booked for",

  "queue.position": "Number {n} in the line",
  "queue.wait": "About {n} min",
  "queue.pending": "We’ll tell you when it’s your turn.",

  "payment.paid": "Paid",
  "payment.unpaid": "Not paid yet",
  "payment.refunded": "Refunded",
  "payment.partially_refunded": "Partly refunded",
  "payment.coupon": "Discount {code} · −{amount}",
  "payment.receipt": "See the receipt",

  "history.moved": "Moved from {from} to {to}",
  "history.byCustomer": "You made this change",
  "history.byAdmin": "We made this change",

  "error.generic": "That didn’t go through.",
  "error.offline": "We couldn’t reach the server.",
  "error.window": "That isn’t possible for this booking any more.",
  "error.slot": "That time has just been taken. Please pick another.",
  "common.close": "Close",
  "common.cancel": "Cancel",
  "common.working": "Working…",
  "common.retry": "Try again",
} as const;

export type ManageStringKey = keyof typeof EN;
type Catalog = Partial<Record<ManageStringKey, string>>;

/** ui-config — de */
const DE: Catalog = {
  "state.loading": "Ihre Buchung wird geöffnet…",
  "dead.invalid.title": "Dieser Link ist nicht mehr gültig",
  "dead.invalid.body":
    "Möglicherweise wurde der Link falsch eingegeben oder die Buchung existiert nicht mehr.",
  "dead.expired.title": "Dieser Link ist abgelaufen",
  "dead.expired.body": "Links aus älteren Bestätigungsmails funktionieren nach einiger Zeit nicht mehr.",
  "dead.revoked.title": "Dieser Link wurde zurückgezogen",
  "dead.revoked.body": "Er wurde ersetzt oder deaktiviert und öffnet Ihre Buchung nicht mehr.",
  "dead.unreachable.title": "Ihre Buchung kann gerade nicht geladen werden",
  "dead.unreachable.body": "Das liegt an uns. Bitte versuchen Sie es in einem Moment erneut.",
  "dead.noLink.title": "Öffnen Sie Ihre Buchung über Ihre Bestätigung",
  "dead.noLink.body": "Diese Seite benötigt den Link aus Ihrer Bestätigungsmail oder SMS.",
  "dead.help": "Die neueste Bestätigung zu dieser Buchung enthält einen funktionierenden Link.",
  "dead.retry": "Erneut versuchen",
  "stale.title": "Ihre Buchung hat sich geändert",
  "stale.body": "Laden Sie sie erneut, um fortzufahren.",
  "stale.action": "Buchung erneut laden",
  "tombstone.title": "Es gibt eine neuere Version dieser Buchung",
  "tombstone.body":
    "So sah die Buchung aus, als dieser Link verschickt wurde. Öffnen Sie die neueste Bestätigung, um Änderungen vorzunehmen.",
  "status.pending": "Ausstehend",
  "status.unconfirmed": "Noch nicht bestätigt",
  "status.confirmed": "Bestätigt",
  "status.rescheduled": "Verschoben",
  "status.cancelled": "Storniert",
  "status.completed": "Abgeschlossen",
  "status.no_show": "Nicht erschienen",
  "status.checked_in": "Eingecheckt",
  "status.waitlisted": "In der Warteschlange",
  "status.payment_pending": "Zahlung ausstehend",
  "section.when": "Wann",
  "section.where": "Wo",
  "section.who": "Ihre Ansprechperson",
  "section.what": "Ihre Angaben zum Termin",
  "section.you": "Ihre Kontaktdaten",
  "section.payment": "Zahlung",
  "section.event": "Ihre Tickets",
  "section.queue": "Ihr Platz in der Warteschlange",
  "section.key": "Türcode",
  "section.history": "Änderungen an dieser Buchung",
  "section.actions": "Buchung verwalten",
  "when.moved": "Verschoben vom {t}",
  "when.local": "Ihre Ortszeit: {t}",
  "when.duration": "{n} Min.",
  "where.join": "Am Videogespräch teilnehmen",
  "where.joinLater": "Der Teilnahmelink erscheint hier am {t}.",
  "where.passcode": "Kenncode {code}",
  "where.callYou": "Wir rufen Sie an: {number}",
  "where.callYouNoNumber": "Wir rufen Sie an",
  "where.callUs": "Bitte rufen Sie {number} an",
  "where.callUsNoNumber": "Bitte rufen Sie uns an",
  "where.map": "In Karten öffnen",
  "who.pending": "Noch nicht zugewiesen",
  "you.masked": "Teilweise verborgen, bis Sie sich bestätigen.",
  "you.reveal": "Meine Daten anzeigen",
  "answers.masked": "Verborgen, bis Sie sich bestätigen",
  "answers.file.scanning": "Wird geprüft",
  "answers.file.blocked": "Diese Datei konnte nicht geprüft werden",
  "answers.download": "Herunterladen",
  "verify.title": "Bestätigen Sie, dass es Sie sind",
  "verify.intro": "Wir senden einen Einmalcode an die Kontaktdaten dieser Buchung.",
  "verify.introRegistration": "Wir senden einen Einmalcode an die Kontaktdaten dieser Anmeldung.",
  "verify.send": "Code senden",
  "verify.sent": "Wir haben einen Code an {target} gesendet.",
  "verify.code": "Code eingeben",
  "verify.submit": "Bestätigen",
  "verify.resend": "Neuen Code senden",
  "verify.wait": "In {n} s können Sie einen neuen Code anfordern.",
  "verify.email": "Per E-Mail senden",
  "verify.sms": "Per SMS senden",
  "verify.error": "Dieser Code stimmt nicht oder ist abgelaufen. Fordern Sie einen neuen an.",
  "verify.exhausted": "Zu viele Versuche. Bitte kontaktieren Sie uns, um fortzufahren.",
  "verify.unavailable": "Für diese Buchung können wir keinen Code senden. Bitte kontaktieren Sie uns.",
  "verify.unavailableRegistration": "Für diese Anmeldung können wir keinen Code senden. Bitte kontaktieren Sie uns.",
  "action.reschedule": "Buchung verschieben",
  "action.cancel": "Buchung stornieren",
  "action.ics": "Zum Kalender hinzufügen",
  "action.pdf": "Zusammenfassung herunterladen",
  "action.export": "Meine Buchungsdaten herunterladen",
  "action.bookAnother": "Etwas anderes buchen",
  "action.history": "Änderungen ansehen",
  "action.optOut": "Keine Erinnerungen zu dieser Buchung",
  "action.optIn": "Wieder Erinnerungen senden",
  "action.optedOut": "Für diese Buchung sind Erinnerungen ausgeschaltet.",
  "resch.title": "Neue Zeit wählen",
  "resch.staff": "Ihre Ansprechperson",
  "resch.anyStaff": "Wer verfügbar ist",
  "resch.none": "In diesem Zeitraum ist nichts frei.",
  "resch.from": "Zeiten anzeigen ab",
  "resch.loading": "Zeiten werden geladen…",
  "resch.loadingFrom": "Zeiten ab {day} werden geladen…",
  "resch.refreshing": "Diese Zeiten werden aktualisiert…",
  "resch.stale": "Diese Zeiten gelten ab {day} und können gerade nicht gewählt werden.",
  "resch.noneFrom": "Ab {day} wurden keine freien Zeiten gefunden. Versuchen Sie ein späteres Datum.",
  "resch.laterHint": "Lieber ein späterer Tag? Ändern Sie oben das Datum.",
  "resch.staffNote": "Bei jeder Zeit steht, wen Sie treffen würden.",
  "resch.zoneNote": "Datum und Uhrzeit gelten für {zone}, wo der Termin stattfindet.",
  "resch.holding": "Diese Zeit wird reserviert…",
  "resch.releasing": "Die reservierte Zeit wird freigegeben…",
  "resch.releaseFailed": "Die reservierte Zeit konnte nicht freigegeben werden. Es hat sich noch nichts geändert.",
  "resch.releasePending": "Ihre neue Auswahl gilt, sobald diese Zeit freigegeben ist.",
  "resch.releaseRetry": "Erneut freigeben",
  "resch.closeAnyway": "Trotzdem schliessen",
  "resch.closeNote": "Wenn Sie jetzt schliessen, bleibt diese Zeit für Sie reserviert, bis die Reservierung abläuft.",
  "resch.staffMismatch": "Zeiten nur mit der gewählten Ansprechperson können wir gerade nicht anzeigen, daher ist keine wählbar. Versuchen Sie es erneut oder lassen Sie die Ansprechperson offen.",
  "resch.confirming": "Ihre Buchung wird verschoben… Bitte lassen Sie dieses Fenster offen, bis es fertig ist.",
  "resch.refreshingBooking": "Die aktuelle Version Ihrer Buchung wird geladen…",
  "resch.refreshFailed": "Die aktuelle Version Ihrer Buchung konnte nicht geladen werden. Diese Zeit kann noch nicht bestätigt werden.",
  "resch.refreshRetry": "Erneut laden",
  "resch.holdExpired": "Die für Sie reservierte Zeit ist abgelaufen und kann nicht mehr bestätigt werden. Wählen Sie sie erneut, um sie wieder zu reservieren, oder wählen Sie eine andere Zeit.",
  "resch.moveUnknown": "Wir haben keine Antwort zur Verschiebung Ihrer Buchung erhalten und wissen daher nicht, ob sie verschoben wurde. Von hier wird nichts erneut gesendet. Schliessen Sie dieses Fenster und laden Sie die Seite neu, um die aktuelle Zeit Ihrer Buchung zu sehen.",
  "resch.more": "Weitere Zeiten anzeigen",
  "resch.held": "Für Sie reserviert bis {t}",
  "resch.confirm": "Diese Zeit bestätigen",
  "resch.moveFromTo": "Von {from} auf {to} verschieben?",
  "resch.moveFromToStaff": "Von {from} auf {to} verschieben, mit {staff}?",
  "resch.reason": "Warum verschieben Sie den Termin?",
  "resch.remaining.one": "Sie können diese Buchung noch {n} Mal verschieben.",
  "resch.remaining.other": "Sie können diese Buchung noch {n} Mal verschieben.",
  "resch.done": "Ihre Buchung wurde verschoben",
  "resch.newLink.email":
    "Wir haben Ihnen den Link zu Ihrer geänderten Buchung per E-Mail gesendet.",
  "resch.newLink.sms":
    "Wir haben Ihnen den Link zu Ihrer geänderten Buchung per SMS gesendet.",
  "cancel.title": "Diese Buchung stornieren?",
  "cancel.body": "Das lässt sich nicht rückgängig machen. Sie müssten neu buchen.",
  "cancel.reason": "Warum stornieren Sie?",
  "cancel.reasonRequired": "Bitte teilen Sie uns mit, warum Sie stornieren.",
  "cancel.deleteData": "Zusätzlich die Löschung meiner Angaben zu dieser Buchung beantragen",
  "cancel.deleteDataRegistration": "Zusätzlich die Löschung meiner Angaben zu dieser Anmeldung beantragen",
  "cancel.confirm": "Ja, stornieren",
  "cancel.keep": "Buchung behalten",
  "conflict.title": "Diese Buchung hat sich zwischenzeitlich geändert",
  "conflict.body": "Wir haben die aktuelle Version geladen. Bitte prüfen und erneut bestätigen.",
  "event.seats.one": "{n} Platz",
  "event.seats.other": "{n} Plätze",
  "event.holders": "Gebucht für",
  "queue.position": "Nummer {n} in der Warteschlange",
  "queue.wait": "Etwa {n} Min.",
  "queue.pending": "Wir melden uns, wenn Sie an der Reihe sind.",
  "payment.paid": "Bezahlt",
  "payment.unpaid": "Noch nicht bezahlt",
  "payment.refunded": "Zurückerstattet",
  "payment.partially_refunded": "Teilweise zurückerstattet",
  "payment.coupon": "Rabatt {code} · −{amount}",
  "payment.receipt": "Quittung ansehen",
  "history.moved": "Verschoben vom {from} auf {to}",
  "history.byCustomer": "Diese Änderung haben Sie vorgenommen",
  "history.byAdmin": "Diese Änderung haben wir vorgenommen",
  "error.generic": "Das hat nicht funktioniert.",
  "error.offline": "Der Server war nicht erreichbar.",
  "error.window": "Das ist für diese Buchung nicht mehr möglich.",
  "error.slot": "Diese Zeit wurde gerade vergeben. Bitte wählen Sie eine andere.",
  "common.close": "Schliessen",
  "common.cancel": "Abbrechen",
  "common.working": "Einen Moment…",
  "common.retry": "Erneut versuchen",
};

/**
 * ui-config — de-CH DELTAS over `de`. Swiss German written usage: no `ß`, and
 * «…» rather than „…“. Everything else inherits, which is why this entry is
 * short by design rather than incomplete — resolution falls through to `de`.
 */
const DE_CH: Catalog = {
  "cancel.body": "Das lässt sich nicht rückgängig machen. Sie müssten neu buchen.",
  "common.close": "Schliessen",
};

/** ui-config — fr */
const FR: Catalog = {
  "state.loading": "Ouverture de votre réservation…",
  "dead.invalid.title": "Ce lien n’est plus valable",
  "dead.invalid.body":
    "Le lien a peut-être été mal saisi, ou il concerne une réservation qui n’existe plus.",
  "dead.expired.title": "Ce lien a expiré",
  "dead.expired.body": "Les liens des anciens e-mails de confirmation cessent de fonctionner après un certain temps.",
  "dead.revoked.title": "Ce lien a été retiré",
  "dead.revoked.body": "Il a été remplacé ou désactivé et n’ouvre plus votre réservation.",
  "dead.unreachable.title": "Impossible de charger votre réservation pour le moment",
  "dead.unreachable.body": "Cela vient de nous. Merci de réessayer dans un instant.",
  "dead.noLink.title": "Ouvrez votre réservation depuis votre confirmation",
  "dead.noLink.body": "Cette page a besoin du lien reçu par e-mail ou par SMS.",
  "dead.help": "Le dernier message de confirmation de cette réservation contient un lien valable.",
  "dead.retry": "Réessayer",
  "stale.title": "Votre réservation a changé",
  "stale.body": "Rechargez-la pour continuer.",
  "stale.action": "Recharger ma réservation",
  "tombstone.title": "Une version plus récente de cette réservation existe",
  "tombstone.body":
    "Voici l’état de la réservation lors de l’envoi de ce lien. Ouvrez le dernier message de confirmation pour la modifier.",
  "status.pending": "En attente",
  "status.unconfirmed": "Pas encore confirmée",
  "status.confirmed": "Confirmée",
  "status.rescheduled": "Déplacée",
  "status.cancelled": "Annulée",
  "status.completed": "Terminée",
  "status.no_show": "Absence",
  "status.checked_in": "Enregistrée",
  "status.waitlisted": "Dans la file",
  "status.payment_pending": "Paiement en attente",
  "section.when": "Quand",
  "section.where": "Où",
  "section.who": "Votre interlocuteur",
  "section.what": "Ce que vous nous avez indiqué",
  "section.you": "Vos coordonnées",
  "section.payment": "Paiement",
  "section.event": "Vos billets",
  "section.queue": "Votre place dans la file",
  "section.key": "Code d’accès",
  "section.history": "Modifications de cette réservation",
  "section.actions": "Gérer cette réservation",
  "when.moved": "Déplacée depuis le {t}",
  "when.local": "Votre heure locale : {t}",
  "when.duration": "{n} min",
  "where.join": "Rejoindre l’appel vidéo",
  "where.joinLater": "Le lien de connexion apparaîtra ici le {t}.",
  "where.passcode": "Code {code}",
  "where.callYou": "Nous vous appellerons au {number}",
  "where.callYouNoNumber": "Nous vous appellerons",
  "where.callUs": "Merci d’appeler le {number}",
  "where.callUsNoNumber": "Merci de nous appeler",
  "where.map": "Ouvrir dans les cartes",
  "who.pending": "Pas encore attribué",
  "you.masked": "En partie masqué jusqu’à ce que vous confirmiez votre identité.",
  "you.reveal": "Afficher mes coordonnées",
  "answers.masked": "Masqué jusqu’à la confirmation de votre identité",
  "answers.file.scanning": "Vérification en cours",
  "answers.file.blocked": "Ce fichier n’a pas pu être vérifié",
  "answers.download": "Télécharger",
  "verify.title": "Confirmez votre identité",
  "verify.intro": "Nous enverrons un code à usage unique aux coordonnées de cette réservation.",
  "verify.introRegistration": "Nous enverrons un code à usage unique aux coordonnées de cette inscription.",
  "verify.send": "Envoyer mon code",
  "verify.sent": "Nous avons envoyé un code à {target}.",
  "verify.code": "Saisissez le code",
  "verify.submit": "Confirmer",
  "verify.resend": "Envoyer un nouveau code",
  "verify.wait": "Vous pourrez demander un nouveau code dans {n} s.",
  "verify.email": "Envoyer par e-mail",
  "verify.sms": "Envoyer par SMS",
  "verify.error": "Ce code est incorrect ou expiré. Demandez-en un nouveau.",
  "verify.exhausted": "Trop de tentatives. Merci de nous contacter pour continuer.",
  "verify.unavailable": "Nous ne pouvons pas envoyer de code pour cette réservation. Merci de nous contacter.",
  "verify.unavailableRegistration": "Nous ne pouvons pas envoyer de code pour cette inscription. Merci de nous contacter.",
  "action.reschedule": "Déplacer cette réservation",
  "action.cancel": "Annuler cette réservation",
  "action.ics": "Ajouter à mon agenda",
  "action.pdf": "Télécharger le récapitulatif",
  "action.export": "Télécharger mes données de réservation",
  "action.bookAnother": "Réserver autre chose",
  "action.history": "Voir les modifications",
  "action.optOut": "Ne plus recevoir de rappels pour cette réservation",
  "action.optIn": "Recevoir à nouveau des rappels",
  "action.optedOut": "Les rappels sont désactivés pour cette réservation.",
  "resch.title": "Choisissez un nouvel horaire",
  "resch.staff": "Votre interlocuteur",
  "resch.anyStaff": "Peu importe qui",
  "resch.none": "Aucun horaire libre sur cette période.",
  "resch.from": "Afficher les horaires à partir du",
  "resch.loading": "Chargement des horaires…",
  "resch.loadingFrom": "Chargement des horaires à partir du {day}…",
  "resch.refreshing": "Mise à jour de ces horaires…",
  "resch.stale": "Ces horaires commencent le {day} et ne peuvent pas être choisis pour le moment.",
  "resch.noneFrom": "Aucun horaire libre trouvé à partir du {day}. Essayez une date plus tardive.",
  "resch.laterHint": "Besoin d’un jour plus tardif ? Changez la date ci-dessus.",
  "resch.staffNote": "Chaque horaire indique votre interlocuteur.",
  "resch.zoneNote": "Les dates et heures sont celles de {zone}, où a lieu le rendez-vous.",
  "resch.holding": "Réservation de cet horaire…",
  "resch.releasing": "Libération de l’horaire réservé…",
  "resch.releaseFailed": "Nous n’avons pas pu libérer l’horaire que vous réservez : rien n’a encore changé.",
  "resch.releasePending": "Votre nouveau choix s’appliquera dès que cet horaire sera libéré.",
  "resch.releaseRetry": "Réessayer de le libérer",
  "resch.closeAnyway": "Fermer quand même",
  "resch.closeNote": "Si vous fermez maintenant, cet horaire reste réservé pour vous jusqu’à son expiration.",
  "resch.staffMismatch": "Nous ne pouvons pas afficher pour l’instant les horaires avec le seul interlocuteur choisi : aucun ne peut être sélectionné. Réessayez, ou choisissez « Peu importe qui ».",
  "resch.confirming": "Déplacement de votre réservation… Merci de garder cette fenêtre ouverte jusqu’à la fin.",
  "resch.refreshingBooking": "Chargement de la version actuelle de votre réservation…",
  "resch.refreshFailed": "Nous n’avons pas pu charger la version actuelle de votre réservation : cet horaire ne peut pas encore être confirmé.",
  "resch.refreshRetry": "Recharger",
  "resch.holdExpired": "L’horaire que nous vous réservions a expiré et ne peut plus être confirmé. Choisissez-le à nouveau pour le réserver, ou choisissez un autre horaire.",
  "resch.moveUnknown": "Nous n’avons pas reçu de réponse sur le déplacement de votre réservation : nous ne savons donc pas si elle a été déplacée. Rien ne sera renvoyé d’ici. Fermez cette fenêtre et rechargez la page pour voir l’horaire actuel de votre réservation.",
  "resch.more": "Afficher d’autres horaires",
  "resch.held": "Réservé pour vous jusqu’à {t}",
  "resch.confirm": "Confirmer cet horaire",
  "resch.moveFromTo": "Déplacer du {from} au {to} ?",
  "resch.moveFromToStaff": "Déplacer du {from} au {to}, avec {staff} ?",
  "resch.reason": "Pourquoi déplacez-vous ce rendez-vous ?",
  "resch.remaining.one": "Vous pouvez encore déplacer cette réservation {n} fois.",
  "resch.remaining.other": "Vous pouvez encore déplacer cette réservation {n} fois.",
  "resch.done": "Votre réservation a été déplacée",
  "resch.newLink.email":
    "Nous vous avons envoyé par e-mail le lien vers votre réservation modifiée.",
  "resch.newLink.sms":
    "Nous vous avons envoyé par SMS le lien vers votre réservation modifiée.",
  "cancel.title": "Annuler cette réservation ?",
  "cancel.body": "Cette action est définitive. Il faudrait réserver à nouveau.",
  "cancel.reason": "Pourquoi annulez-vous ?",
  "cancel.reasonRequired": "Merci d’indiquer la raison de votre annulation.",
  "cancel.deleteData": "Demander aussi la suppression des informations données pour cette réservation",
  "cancel.deleteDataRegistration": "Demander aussi la suppression des informations données pour cette inscription",
  "cancel.confirm": "Oui, annuler",
  "cancel.keep": "Conserver ma réservation",
  "conflict.title": "Cette réservation a changé pendant votre visite",
  "conflict.body": "Nous avons chargé la version actuelle. Merci de la vérifier et de confirmer à nouveau.",
  "event.seats.one": "{n} place",
  "event.seats.other": "{n} places",
  "event.holders": "Réservé pour",
  "queue.position": "Numéro {n} dans la file",
  "queue.wait": "Environ {n} min",
  "queue.pending": "Nous vous prévenons dès que c’est votre tour.",
  "payment.paid": "Payé",
  "payment.unpaid": "Pas encore payé",
  "payment.refunded": "Remboursé",
  "payment.partially_refunded": "Partiellement remboursé",
  "payment.coupon": "Réduction {code} · −{amount}",
  "payment.receipt": "Voir le reçu",
  "history.moved": "Déplacée du {from} au {to}",
  "history.byCustomer": "Cette modification vient de vous",
  "history.byAdmin": "Cette modification vient de nous",
  "error.generic": "Cela n’a pas fonctionné.",
  "error.offline": "Le serveur est injoignable.",
  "error.window": "Ce n’est plus possible pour cette réservation.",
  "error.slot": "Cet horaire vient d’être pris. Merci d’en choisir un autre.",
  "common.close": "Fermer",
  "common.cancel": "Annuler",
  "common.working": "Un instant…",
  "common.retry": "Réessayer",
};

/** ui-config — it */
const IT: Catalog = {
  "state.loading": "Apertura della sua prenotazione…",
  "dead.invalid.title": "Questo link non è più valido",
  "dead.invalid.body":
    "Il link potrebbe essere stato digitato male oppure riguardare una prenotazione che non esiste più.",
  "dead.expired.title": "Questo link è scaduto",
  "dead.expired.body": "I link delle e-mail di conferma più vecchie dopo un po’ non funzionano più.",
  "dead.revoked.title": "Questo link è stato revocato",
  "dead.revoked.body": "È stato sostituito o disattivato e non apre più la sua prenotazione.",
  "dead.unreachable.title": "Al momento non riusciamo a caricare la sua prenotazione",
  "dead.unreachable.body": "Dipende da noi. La preghiamo di riprovare tra un momento.",
  "dead.noLink.title": "Apra la prenotazione dalla sua conferma",
  "dead.noLink.body": "Questa pagina ha bisogno del link ricevuto per e-mail o SMS.",
  "dead.help": "L’ultimo messaggio di conferma di questa prenotazione contiene un link valido.",
  "dead.retry": "Riprova",
  "stale.title": "La sua prenotazione è cambiata",
  "stale.body": "La ricarichi per continuare.",
  "stale.action": "Ricarica la prenotazione",
  "tombstone.title": "Esiste una versione più recente di questa prenotazione",
  "tombstone.body":
    "Questa è la prenotazione come era quando è stato inviato il link. Apra l’ultimo messaggio di conferma per modificarla.",
  "status.pending": "In attesa",
  "status.unconfirmed": "Non ancora confermata",
  "status.confirmed": "Confermata",
  "status.rescheduled": "Spostata",
  "status.cancelled": "Annullata",
  "status.completed": "Conclusa",
  "status.no_show": "Assente",
  "status.checked_in": "Registrata",
  "status.waitlisted": "In coda",
  "status.payment_pending": "Pagamento in attesa",
  "section.when": "Quando",
  "section.where": "Dove",
  "section.who": "Chi la riceve",
  "section.what": "Quello che ci ha indicato",
  "section.you": "I suoi dati",
  "section.payment": "Pagamento",
  "section.event": "I suoi biglietti",
  "section.queue": "Il suo posto in coda",
  "section.key": "Codice d’accesso",
  "section.history": "Modifiche a questa prenotazione",
  "section.actions": "Gestisci la prenotazione",
  "when.moved": "Spostata dal {t}",
  "when.local": "La sua ora locale: {t}",
  "when.duration": "{n} min",
  "where.join": "Partecipa alla videochiamata",
  "where.joinLater": "Il link per partecipare apparirà qui il {t}.",
  "where.passcode": "Codice {code}",
  "where.callYou": "La chiameremo al {number}",
  "where.callYouNoNumber": "La chiameremo",
  "where.callUs": "La preghiamo di chiamare il {number}",
  "where.callUsNoNumber": "La preghiamo di chiamarci",
  "where.map": "Apri nelle mappe",
  "who.pending": "Non ancora assegnato",
  "you.masked": "In parte nascosti finché non conferma la sua identità.",
  "you.reveal": "Mostra i miei dati",
  "answers.masked": "Nascosto finché non conferma la sua identità",
  "answers.file.scanning": "In verifica",
  "answers.file.blocked": "Non è stato possibile verificare questo file",
  "answers.download": "Scarica",
  "verify.title": "Confermi la sua identità",
  "verify.intro": "Invieremo un codice monouso ai contatti di questa prenotazione.",
  "verify.introRegistration": "Invieremo un codice monouso ai contatti di questa iscrizione.",
  "verify.send": "Invia il codice",
  "verify.sent": "Abbiamo inviato un codice a {target}.",
  "verify.code": "Inserisca il codice",
  "verify.submit": "Conferma",
  "verify.resend": "Invia un nuovo codice",
  "verify.wait": "Potrà richiedere un nuovo codice tra {n} s.",
  "verify.email": "Invia per e-mail",
  "verify.sms": "Invia per SMS",
  "verify.error": "Il codice non è corretto oppure è scaduto. Ne richieda uno nuovo.",
  "verify.exhausted": "Troppi tentativi. La preghiamo di contattarci per continuare.",
  "verify.unavailable": "Per questa prenotazione non possiamo inviare un codice. La preghiamo di contattarci.",
  "verify.unavailableRegistration": "Per questa iscrizione non possiamo inviare un codice. La preghiamo di contattarci.",
  "action.reschedule": "Sposta la prenotazione",
  "action.cancel": "Annulla la prenotazione",
  "action.ics": "Aggiungi al calendario",
  "action.pdf": "Scarica il riepilogo",
  "action.export": "Scarica i dati della prenotazione",
  "action.bookAnother": "Prenota altro",
  "action.history": "Vedi le modifiche",
  "action.optOut": "Non inviare promemoria per questa prenotazione",
  "action.optIn": "Invia di nuovo i promemoria",
  "action.optedOut": "I promemoria per questa prenotazione sono disattivati.",
  "resch.title": "Scelga un nuovo orario",
  "resch.staff": "Chi la riceve",
  "resch.anyStaff": "Chiunque sia disponibile",
  "resch.none": "In questo periodo non ci sono orari liberi.",
  "resch.from": "Mostra orari dal",
  "resch.loading": "Caricamento degli orari…",
  "resch.loadingFrom": "Caricamento degli orari dal {day}…",
  "resch.refreshing": "Aggiornamento di questi orari…",
  "resch.stale": "Questi orari partono dal {day} e al momento non si possono scegliere.",
  "resch.noneFrom": "Nessun orario libero trovato dal {day}. Provi una data successiva.",
  "resch.laterHint": "Preferisce un giorno successivo? Cambi la data qui sopra.",
  "resch.staffNote": "Per ogni orario è indicato chi la riceverebbe.",
  "resch.zoneNote": "Date e orari sono riferiti a {zone}, dove si svolge l’appuntamento.",
  "resch.holding": "Prenotazione di questo orario…",
  "resch.releasing": "Rilascio dell’orario riservato…",
  "resch.releaseFailed": "Non è stato possibile rilasciare l’orario che sta riservando: per ora non è cambiato nulla.",
  "resch.releasePending": "La nuova scelta si applicherà quando quell’orario sarà rilasciato.",
  "resch.releaseRetry": "Riprova a rilasciarlo",
  "resch.closeAnyway": "Chiudi comunque",
  "resch.closeNote": "Se chiude ora, quell’orario resta riservato per lei fino alla scadenza.",
  "resch.staffMismatch": "Al momento non possiamo mostrare orari solo con la persona scelta, quindi nessuno è selezionabile. Riprovi o scelga «Chiunque sia disponibile».",
  "resch.confirming": "Spostamento della prenotazione… Tenga aperta questa finestra fino al termine.",
  "resch.refreshingBooking": "Caricamento della versione attuale della sua prenotazione…",
  "resch.refreshFailed": "Non è stato possibile caricare la versione attuale della sua prenotazione: questo orario non può ancora essere confermato.",
  "resch.refreshRetry": "Ricarica",
  "resch.holdExpired": "L’orario che teneva riservato è scaduto e non può più essere confermato. Lo scelga di nuovo per riservarlo, oppure scelga un altro orario.",
  "resch.moveUnknown": "Non abbiamo ricevuto risposta sullo spostamento della sua prenotazione, quindi non sappiamo se è stata spostata. Da qui non verrà inviato nulla di nuovo. Chiuda questa finestra e ricarichi la pagina per vedere l’orario attuale della sua prenotazione.",
  "resch.more": "Mostra altri orari",
  "resch.held": "Riservato per lei fino alle {t}",
  "resch.confirm": "Conferma questo orario",
  "resch.moveFromTo": "Spostare da {from} a {to}?",
  "resch.moveFromToStaff": "Spostare da {from} a {to}, con {staff}?",
  "resch.reason": "Perché sta spostando l’appuntamento?",
  "resch.remaining.one": "Può spostare questa prenotazione ancora {n} volta.",
  "resch.remaining.other": "Può spostare questa prenotazione ancora {n} volte.",
  "resch.done": "La sua prenotazione è stata spostata",
  "resch.newLink.email":
    "Le abbiamo inviato per e-mail il link alla sua prenotazione aggiornata.",
  "resch.newLink.sms":
    "Le abbiamo inviato per SMS il link alla sua prenotazione aggiornata.",
  "cancel.title": "Annullare questa prenotazione?",
  "cancel.body": "L’operazione non si può annullare. Dovrebbe prenotare di nuovo.",
  "cancel.reason": "Perché sta annullando?",
  "cancel.reasonRequired": "Ci indichi il motivo dell’annullamento.",
  "cancel.deleteData": "Chiedo anche la cancellazione dei dati forniti per questa prenotazione",
  "cancel.deleteDataRegistration": "Chiedo anche la cancellazione dei dati forniti per questa iscrizione",
  "cancel.confirm": "Sì, annulla",
  "cancel.keep": "Mantieni la prenotazione",
  "conflict.title": "Questa prenotazione è cambiata mentre era qui",
  "conflict.body": "Abbiamo caricato la versione attuale. La controlli e confermi di nuovo.",
  "event.seats.one": "{n} posto",
  "event.seats.other": "{n} posti",
  "event.holders": "Prenotato per",
  "queue.position": "Numero {n} in coda",
  "queue.wait": "Circa {n} min",
  "queue.pending": "La avvisiamo quando è il suo turno.",
  "payment.paid": "Pagato",
  "payment.unpaid": "Non ancora pagato",
  "payment.refunded": "Rimborsato",
  "payment.partially_refunded": "Rimborsato in parte",
  "payment.coupon": "Sconto {code} · −{amount}",
  "payment.receipt": "Vedi la ricevuta",
  "history.moved": "Spostata dal {from} al {to}",
  "history.byCustomer": "Questa modifica l’ha fatta lei",
  "history.byAdmin": "Questa modifica l’abbiamo fatta noi",
  "error.generic": "Non è stato possibile completare l’operazione.",
  "error.offline": "Non è stato possibile raggiungere il server.",
  "error.window": "Per questa prenotazione non è più possibile.",
  "error.slot": "Questo orario è stato appena occupato. Ne scelga un altro.",
  "common.close": "Chiudi",
  "common.cancel": "Annulla",
  "common.working": "Un momento…",
  "common.retry": "Riprova",
};

/** ui-config — one entry per shipped locale. */
const CATALOG: Record<string, Catalog> = {
  "en-US": EN,
  en: EN,
  de: DE,
  "de-CH": DE_CH,
  fr: FR,
  it: IT,
};

/* ── resolution ──────────────────────────────────────────────────────────────── */

export interface ManageIntl {
  locale: string;
  t: (key: ManageStringKey, params?: Record<string, string | number>) => string;
  /** CLDR-categorised plural of `key.<category>`, `other` required. */
  plural: (key: string, n: number, params?: Record<string, string | number>) => string;
  /** Locale-formatted number — never `String(n)`. */
  number: (n: number) => string;
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/gu, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : whole,
  );
}

/**
 * The page locale: the DOCUMENT's `lang` first (the manage host stamps it, and
 * it is what the server composed `view.texts` against), then the browser's, then
 * `en`. Never guessed from the tenant or from a timezone.
 */
export function pageLocale(): string {
  const declared = typeof document !== "undefined"
    ? document.documentElement.getAttribute("lang")?.trim()
    : "";
  if (declared) return declared;
  const navigatorLocale = typeof navigator !== "undefined" ? navigator.language : "";
  return navigatorLocale || "en";
}

export function manageIntl(locale: string): ManageIntl {
  const base = locale.split("-")[0] ?? locale;
  const chain = [CATALOG[locale], CATALOG[base], EN];

  const resolve = (key: string): string | null => {
    for (const table of chain) {
      const value = table?.[key as ManageStringKey];
      if (typeof value === "string" && value) return value;
    }
    return null;
  };

  let rules: Intl.PluralRules | null = null;
  let numbers: Intl.NumberFormat | null = null;
  try {
    rules = new Intl.PluralRules(locale);
    numbers = new Intl.NumberFormat(locale);
  } catch {
    rules = new Intl.PluralRules("en");
    numbers = new Intl.NumberFormat("en");
  }

  const number = (n: number) => (numbers ? numbers.format(n) : String(n));

  /** A missing key renders its own name rather than a blank — a loud hole. */
  const t: ManageIntl["t"] = (key, params) => {
    const template = resolve(key);
    return template === null ? key : interpolate(template, params);
  };

  /**
   * `Intl.PluralRules().select(n)` picks the category; `other` is the required
   * fallback, so a locale whose category this catalog does not carry still
   * renders a sentence. `n` is formatted with `Intl.NumberFormat`, never pasted
   * in as a raw JS number.
   */
  const plural: ManageIntl["plural"] = (key, n, params) => {
    const category = rules ? rules.select(n) : n === 1 ? "one" : "other";
    const template = resolve(`${key}.${category}`) ?? resolve(`${key}.other`);
    const values = { n: number(n), ...params };
    return template === null ? `${key}.other` : interpolate(template, values);
  };

  return { locale, t, plural, number };
}
